import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  Tooltip,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { AuthContext } from 'components/authentication/context/authContext';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';

// ---- types ----

interface LinkedInvoice {
  id: number;
  number: string;
  date: string;
  from: string;
  to: string;
  amount: number;
  currency?: { code: string };
  allocatedAmount: number | null; // null = legacy link
}

interface UnreconciledInvoice {
  id: number;
  number: string;
  date: string;
  from: string;
  to: string;
  amount: number;
  remainingAmount: number;
  reconciliationStatus: 'None' | 'Partial' | 'Full';
  currency?: { code: string };
}

interface SeatInfo {
  id: number;
  amount: number;
  incoming: number;
  date: string;
  description: string;
  account?: { name: string; accountNumber: string };
  idCurrency?: number;
  currency?: { code: string };
}

// ---- helpers ----

const formatCurrency = (amount: number, code = '') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code || 'USD' }).format(amount);
  } catch {
    return `${code} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }
};

// Map the raw API response (LinkedInvoiceResult from backend) → LinkedInvoice
function mapLinked(raw: any): LinkedInvoice {
  const inv = raw.invoice ?? raw;
  return {
    id: inv.id,
    number: inv.number,
    date: inv.date,
    from: inv.from,
    to: inv.to,
    amount: inv.amount,
    currency: inv.currency,
    allocatedAmount: raw.allocatedAmount ?? null,
  };
}

// Map the raw API response (UnreconciledInvoiceResult from backend) → UnreconciledInvoice
function mapUnreconciled(raw: any): UnreconciledInvoice {
  const inv = raw.invoice ?? raw;
  return {
    id: inv.id,
    number: inv.number,
    date: inv.date,
    from: inv.from,
    to: inv.to,
    amount: inv.amount,
    remainingAmount: raw.remainingAmount ?? inv.amount,
    reconciliationStatus: raw.reconciliationStatus ?? 'None',
    currency: inv.currency,
  };
}

// ---- component ----

const ReconciliationPage: React.FC = () => {
  const { accountId, seatId } = useParams<{ accountId: string; seatId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDebit = searchParams.get('incoming') === '0';
  const api: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const { success: toastSuccess, error: toastError } = useToastMessageQueue();

  const parsedSeatId = parseInt(seatId || '0', 10);
  const parsedAccountId = parseInt(accountId || '0', 10);

  // Seat info
  const [seat, setSeat] = useState<SeatInfo | null>(null);
  const [seatLoading, setSeatLoading] = useState(true);

  // Already-linked invoices
  const [linkedInvoices, setLinkedInvoices] = useState<LinkedInvoice[]>([]);
  const [linkedLoading, setLinkedLoading] = useState(true);

  // Unreconciled invoice search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UnreconciledInvoice[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Selection: invoiceId → allocated amount the user wants to assign
  const [allocations, setAllocations] = useState<Record<number, string>>({});

  // Actions
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  // Fetch seat details
  const loadSeat = useCallback(async () => {
    if (!parsedSeatId) return;
    setSeatLoading(true);
    try {
      const res = await (api.invoiceApi as any).apiVversionLedgerIdGet(parsedSeatId, '1');
      setSeat(res.data?.data ?? null);
    } catch {
      setSeat(null);
    } finally {
      setSeatLoading(false);
    }
  }, [api, parsedSeatId]);

  // Fetch already-linked invoices (now includes allocatedAmount)
  const loadLinked = useCallback(async () => {
    if (!parsedSeatId) return;
    setLinkedLoading(true);
    try {
      const res = await api.invoiceApiWrapper.getSeatReconciliation('1', parsedSeatId);
      const raw: any[] = res.data?.data ?? [];
      setLinkedInvoices(raw.map(mapLinked));
    } catch {
      setLinkedInvoices([]);
    } finally {
      setLinkedLoading(false);
    }
  }, [api, parsedSeatId]);

  useEffect(() => {
    loadSeat();
    loadLinked();
  }, [loadSeat, loadLinked]);

  // Derived amounts
  // Use allocatedAmount from the link if present; fall back to invoice amount for legacy links
  // Seat amount is stored in the DB multiplied by 100 (integer cents); divide before display/calculation.
  const seatAmount = seat ? seat.amount / 100 : 0;
  const linkedAllocated = linkedInvoices.reduce((sum, inv) =>
    sum + (inv.allocatedAmount !== null ? inv.allocatedAmount : inv.amount), 0);
  const seatRemaining = seatAmount - linkedAllocated;
  const currencyCode = seat?.currency?.code || '';

  const allocationPct = seatAmount > 0
    ? Math.min(100, (linkedAllocated / seatAmount) * 100)
    : 0;

  // Search unreconciled invoices
  const searchUnreconciled = useCallback(async (term: string) => {
    setSearchLoading(true);
    try {
      let results: UnreconciledInvoice[] = [];
      const seatCurrencyId = seat?.idCurrency ?? 0;
      if (isDebit) {
        const res = await api.invoiceApiWrapper.searchApUnreconciled('1', term, 1, 50, seatCurrencyId);
        results = (res.data?.data?.queryResult ?? []).map(mapUnreconciled);
      } else {
        const res = await api.invoiceApiWrapper.searchPaidUnreconciled('1', term, 1, 50, seatCurrencyId);
        results = (res.data?.data?.queryResult ?? []).map(mapUnreconciled);
      }
      // Exclude invoices already linked to this seat
      const linkedIds = new Set(linkedInvoices.map((i) => i.id));
      setSearchResults(results.filter((r) => !linkedIds.has(r.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [api, isDebit, linkedInvoices, seat]);

  useEffect(() => {
    const timer = setTimeout(() => searchUnreconciled(searchTerm), 350);
    return () => clearTimeout(timer);
  }, [searchTerm, searchUnreconciled]);

  // Toggle invoice selection; pre-fill allocation with the invoice remaining amount
  const handleToggleSelect = (inv: UnreconciledInvoice) => {
    setAllocations((prev) => {
      if (inv.id in prev) {
        const next = { ...prev };
        delete next[inv.id];
        return next;
      }
      return { ...prev, [inv.id]: inv.remainingAmount.toFixed(2) };
    });
  };

  const handleAllocationChange = (invoiceId: number, value: string) => {
    setAllocations((prev) => ({ ...prev, [invoiceId]: value }));
  };

  const handleSelectAll = (checked: boolean) => {
    if (!checked) {
      setAllocations({});
      return;
    }
    const next: Record<number, string> = {};
    for (const inv of searchResults) {
      next[inv.id] = inv.remainingAmount.toFixed(2);
    }
    setAllocations(next);
  };

  const selectedIds = Object.keys(allocations).map(Number);

  const handleAddSelected = async () => {
    if (selectedIds.length === 0) return;
    // Validate amounts
    const invoiceAllocations: Record<number, number | null> = {};
    for (const id of selectedIds) {
      const val = parseFloat(allocations[id] || '0');
      if (isNaN(val) || val <= 0) {
        toastError({ message: intl.get('reconciliation.invalidAmount') || 'All allocated amounts must be greater than 0.' });
        return;
      }
      invoiceAllocations[id] = val;
    }
    setSaving(true);
    try {
      await api.invoiceApiWrapper.reconcileSeat('1', parsedSeatId, invoiceAllocations);
      toastSuccess({ message: intl.get('reconciliation.linked') || 'Invoices linked successfully.' });
      setAllocations({});
      await loadLinked();
      setSearchResults((prev) => prev.filter((r) => !invoiceAllocations[r.id]));
    } catch {
      toastError({ message: intl.get('reconciliation.errorLinking') || 'Failed to link invoices.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLinked = async (invoiceId: number) => {
    setRemovingId(invoiceId);
    try {
      await api.invoiceApiWrapper.unreconcileSeat('1', parsedSeatId, invoiceId);
      toastSuccess({ message: intl.get('reconciliation.unlinked') || 'Invoice unlinked.' });
      setLinkedInvoices((prev) => prev.filter((i) => i.id !== invoiceId));
    } catch {
      toastError({ message: intl.get('reconciliation.errorUnlinking') || 'Failed to unlink invoice.' });
    } finally {
      setRemovingId(null);
    }
  };

  if (seatLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!seat) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {intl.get('reconciliation.seatNotFound') || 'Ledger entry not found.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <Icon icon="mdi:bank-check" width={24} />
          <Typography variant="h6">
            {intl.get('reconciliation.title') || 'Reconciliation'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Icon icon="mdi:arrow-left" />}
          onClick={() => navigate(`/account/detail/${parsedAccountId}`)}
        >
          {intl.get('common.back') || 'Back'}
        </Button>
      </Box>

      {/* Seat summary card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {intl.get('reconciliation.ledgerEntry') || 'Ledger Entry'}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Chip
              label={seat.incoming === 1
                ? (intl.get('ledgerLink.credit') || 'Credit')
                : (intl.get('ledgerLink.debit') || 'Debit')}
              color={seat.incoming === 1 ? 'success' : 'error'}
              size="small"
            />
            <Typography variant="h5" fontWeight="bold" fontFamily="monospace">
              {formatCurrency(seatAmount, currencyCode)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(seat.date).toLocaleDateString()}
            </Typography>
            {seat.account && (
              <Typography variant="body2" color="text.secondary">
                {seat.account.name} ({seat.account.accountNumber})
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {seat.description}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Allocation progress */}
          <Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                {intl.get('reconciliation.allocated') || 'Allocated'}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(linkedAllocated, currencyCode)}
                {' / '}
                {formatCurrency(seatAmount, currencyCode)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={allocationPct}
              color={allocationPct >= 100 ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Box display="flex" justifyContent="flex-end" mt={0.5}>
              <Typography
                variant="caption"
                color={seatRemaining < 0 ? 'error.main' : 'text.secondary'}
              >
                {intl.get('reconciliation.remaining') || 'Remaining:'}
                {' '}
                {formatCurrency(seatRemaining, currencyCode)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Linked invoices */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:file-check-outline" width={20} />
            {intl.get('reconciliation.linkedInvoices') || 'Linked Invoices'}
            <Chip label={linkedInvoices.length} size="small" />
          </Typography>

          {linkedLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : linkedInvoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              {intl.get('reconciliation.noLinkedInvoices') || 'No invoices linked yet.'}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{intl.get('invoice.number') || 'Number'}</TableCell>
                  <TableCell>{intl.get('invoice.date') || 'Date'}</TableCell>
                  <TableCell>{intl.get('invoice.from') || 'From'}</TableCell>
                  <TableCell align="right">{intl.get('invoice.amount') || 'Invoice Total'}</TableCell>
                  <TableCell align="right">{intl.get('reconciliation.allocatedAmount') || 'Allocated'}</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {linkedInvoices.map((inv) => {
                  const allocated = inv.allocatedAmount;
                  const remaining = allocated !== null ? inv.amount - allocated : null;
                  return (
                    <TableRow key={inv.id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">{inv.number}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(inv.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>{inv.from}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontFamily="monospace">
                          {formatCurrency(inv.amount, inv.currency?.code || currencyCode)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {allocated !== null ? (
                          <Box textAlign="right">
                            <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main">
                              {formatCurrency(allocated, inv.currency?.code || currencyCode)}
                            </Typography>
                            {remaining !== null && remaining > 0 && (
                              <Typography variant="caption" color="warning.main">
                                {formatCurrency(remaining, inv.currency?.code || currencyCode)} {intl.get('reconciliation.remaining') || 'remaining'}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled" fontFamily="monospace">
                            {intl.get('common.notAvailable') || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={intl.get('reconciliation.removeLink') || 'Remove link'}>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={removingId === inv.id}
                            onClick={() => handleRemoveLinked(inv.id)}
                          >
                            {removingId === inv.id
                              ? <CircularProgress size={16} />
                              : <Icon icon="mdi:link-variant-off" width={16} />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Search and add unreconciled invoices */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:file-search-outline" width={20} />
            {isDebit
              ? (intl.get('reconciliation.addApInvoices') || 'Add AP Invoices')
              : (intl.get('reconciliation.addInvoices') || 'Add Invoices')}
          </Typography>

          <TextField
            size="small"
            fullWidth
            placeholder={isDebit
              ? (intl.get('reconciliation.searchApPlaceholder') || 'Search by invoice number, supplier, or amount')
              : (intl.get('reconciliation.searchPlaceholder') || 'Search by invoice number, vendor, or amount')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><Icon icon="mdi:magnify" width={18} /></InputAdornment>,
                endAdornment: searchLoading ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
              }
            }}
            sx={{ mb: 2 }}
          />

          {searchResults.length === 0 && !searchLoading ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              {isDebit
                ? (intl.get('reconciliation.noApInvoices') || 'No AP invoices found.')
                : (intl.get('reconciliation.noUnreconciled') || 'No unreconciled paid invoices found.')}
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={selectedIds.length > 0 && selectedIds.length < searchResults.length}
                        checked={searchResults.length > 0 && selectedIds.length === searchResults.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>{intl.get('invoice.number') || 'Number'}</TableCell>
                    <TableCell>{intl.get('invoice.date') || 'Date'}</TableCell>
                    <TableCell>{intl.get('invoice.from') || 'From'}</TableCell>
                    <TableCell align="right">{intl.get('invoice.amount') || 'Invoice Total'}</TableCell>
                    <TableCell align="right">{intl.get('reconciliation.remaining') || 'Remaining'}</TableCell>
                    <TableCell align="right" sx={{ minWidth: 140 }}>{intl.get('reconciliation.allocateAmount') || 'Allocate'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((inv) => {
                    const isSelected = inv.id in allocations;
                    const isPartial = inv.reconciliationStatus === 'Partial';
                    return (
                      <TableRow
                        key={inv.id}
                        hover
                        selected={isSelected}
                        onClick={() => !isSelected && handleToggleSelect(inv)}
                        sx={{ cursor: isSelected ? 'default' : 'pointer' }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => { e.stopPropagation(); handleToggleSelect(inv); }}>
                          <Checkbox size="small" checked={isSelected} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">{inv.number}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(inv.date).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{inv.from}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontFamily="monospace">
                            {formatCurrency(inv.amount, inv.currency?.code || currencyCode)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            color={isPartial ? 'warning.main' : 'text.primary'}
                            fontWeight={isPartial ? 600 : 400}
                          >
                            {formatCurrency(inv.remainingAmount, inv.currency?.code || currencyCode)}
                            {isPartial && (
                              <Chip label={intl.get('reconciliation.partial') || 'Partial'} size="small" color="warning" sx={{ ml: 0.5, height: 16, fontSize: 10 }} />
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {isSelected ? (
                            <TextField
                              size="small"
                              type="number"
                              value={allocations[inv.id]}
                              onChange={(e) => handleAllocationChange(inv.id, e.target.value)}
                              slotProps={{
                                input: { inputProps: { min: 0.01, max: inv.remainingAmount, step: 0.01 } }
                              }}
                              sx={{ width: 130 }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {selectedIds.length > 0 && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedIds.length} {intl.get('reconciliation.invoicesSelected') || 'invoice(s) selected'}
                    {' · '}
                    {intl.get('reconciliation.totalToAllocate') || 'Total to allocate:'}
                    {' '}
                    <strong>
                      {formatCurrency(
                        selectedIds.reduce((s, id) => s + (parseFloat(allocations[id] || '0') || 0), 0),
                        currencyCode
                      )}
                    </strong>
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Icon icon="mdi:link-variant-plus" />}
                    disabled={saving}
                    onClick={handleAddSelected}
                  >
                    {intl.get('reconciliation.linkSelected') || `Link ${selectedIds.length} invoice(s)`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ReconciliationPage;
